import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { RTL_LANGUAGES, type SupportedLanguage } from './languages';
import { loadLocaleInto } from './locale-loader';

const LANGUAGE_OPTIONS: { code: SupportedLanguage; label: string; nativeLabel: string }[] = [
  { code: 'en', label: 'English', nativeLabel: 'English' },
  { code: 'nl', label: 'Dutch', nativeLabel: 'Nederlands' },
  { code: 'de', label: 'German', nativeLabel: 'Deutsch' },
  { code: 'fr', label: 'French', nativeLabel: 'Français' },
  { code: 'es', label: 'Spanish', nativeLabel: 'Español' },
  { code: 'it', label: 'Italian', nativeLabel: 'Italiano' },
  { code: 'pt', label: 'Portuguese', nativeLabel: 'Português' },
  { code: 'bg', label: 'Bulgarian', nativeLabel: 'Български' },
  { code: 'cs', label: 'Czech', nativeLabel: 'Čeština' },
  { code: 'da', label: 'Danish', nativeLabel: 'Dansk' },
  { code: 'el', label: 'Greek', nativeLabel: 'Ελληνικά' },
  { code: 'et', label: 'Estonian', nativeLabel: 'Eesti' },
  { code: 'fi', label: 'Finnish', nativeLabel: 'Suomi' },
  { code: 'ga', label: 'Irish', nativeLabel: 'Gaeilge' },
  { code: 'hr', label: 'Croatian', nativeLabel: 'Hrvatski' },
  { code: 'hu', label: 'Hungarian', nativeLabel: 'Magyar' },
  { code: 'is', label: 'Icelandic', nativeLabel: 'Íslenska' },
  { code: 'lt', label: 'Lithuanian', nativeLabel: 'Lietuvių' },
  { code: 'lv', label: 'Latvian', nativeLabel: 'Latviešu' },
  { code: 'mt', label: 'Maltese', nativeLabel: 'Malti' },
  { code: 'nb', label: 'Norwegian', nativeLabel: 'Norsk bokmål' },
  { code: 'pl', label: 'Polish', nativeLabel: 'Polski' },
  { code: 'ro', label: 'Romanian', nativeLabel: 'Română' },
  { code: 'sk', label: 'Slovak', nativeLabel: 'Slovenčina' },
  { code: 'sl', label: 'Slovenian', nativeLabel: 'Slovenščina' },
  { code: 'sv', label: 'Swedish', nativeLabel: 'Svenska' },
  { code: 'ml', label: 'Malayalam', nativeLabel: 'മലയാളം' },
  { code: 'hi', label: 'Hindi', nativeLabel: 'हिन्दी' },
  { code: 'ta', label: 'Tamil', nativeLabel: 'தமிழ்' },
  { code: 'te', label: 'Telugu', nativeLabel: 'తెలుగు' },
  { code: 'kn', label: 'Kannada', nativeLabel: 'ಕನ್ನಡ' },
  { code: 'bn', label: 'Bengali', nativeLabel: 'বাংলা' },
  { code: 'ar', label: 'Arabic', nativeLabel: 'العربية' },
  { code: 'ur', label: 'Urdu', nativeLabel: 'اردو' },
];

const LANGUAGE_LABELS: Record<SupportedLanguage, string> = Object.fromEntries(
  LANGUAGE_OPTIONS.map((opt) => [opt.code, opt.nativeLabel]),
) as Record<SupportedLanguage, string>;

/**
 * Hook for managing the app language.
 * Wraps react-i18next's useTranslation and provides typed language switching.
 * <html lang> and <html dir> are kept in step by the i18n config, so RTL for
 * Arabic and Urdu applies on first paint as well as on switch.
 */
/**
 * Locale chunks resolve out of order, so a slow earlier choice must not land
 * after a quick later one. Module scope, not a ref, because the language is one
 * global and any component may set it.
 */
let latestRequest = 0;

/** Set when the person picks a language on this device (switcher, onboarding, landing). */
const EXPLICIT_KEY = 'unipostudio-language-explicit';

export function markExplicitLanguageChoice(): void {
  try {
    localStorage.setItem(EXPLICIT_KEY, '1');
  } catch {
    // Storage unavailable: the choice still applies to this page.
  }
}

/**
 * Whether the language on screen was chosen by the person on this device,
 * rather than detected from the browser. Only a chosen one is written back
 * to their account (useSyncUiLanguage); a detected one gives way to the
 * language their account already has.
 */
export function hasExplicitLanguageChoice(): boolean {
  try {
    return localStorage.getItem(EXPLICIT_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Loads a language's strings, then switches to it. When the strings cannot be
 * loaded the screen stays in the language it is in (it used to switch to,
 * say, Arabic and right-to-left with English strings). A later call wins over
 * an earlier one still loading ('superseded').
 */
export async function applyLanguage(
  i18n: { changeLanguage: (lng: string) => unknown } & Parameters<typeof loadLocaleInto>[0],
  lang: SupportedLanguage,
): Promise<'switched' | 'failed' | 'superseded'> {
  const request = ++latestRequest;
  const loaded = await loadLocaleInto(i18n, lang);
  if (request !== latestRequest) return 'superseded';
  if (!loaded) return 'failed';
  await i18n.changeLanguage(lang);
  document.documentElement.lang = lang;
  document.documentElement.dir = RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr';
  return 'switched';
}

export function useLanguage() {
  const { i18n, t } = useTranslation();

  const currentLanguage = (i18n.language?.slice(0, 2) ?? 'en') as SupportedLanguage;
  const isRtl = RTL_LANGUAGES.includes(currentLanguage);

  const setLanguage = useCallback(
    (lang: SupportedLanguage) => {
      // Strings for a language arrive as their own chunk, so they are fetched
      // before switching; changeLanguage on its own would show English until
      // they land, and a failed fetch would leave English under the new dir.
      markExplicitLanguageChoice();
      const previous = { lang: document.documentElement.lang, dir: document.documentElement.dir };
      // Set eagerly: callers (and tests) expect the document to reflect the
      // choice at once; put back if the strings never arrive.
      document.documentElement.lang = lang;
      document.documentElement.dir = RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr';
      void applyLanguage(i18n, lang).then((outcome) => {
        if (outcome !== 'failed') return;
        document.documentElement.lang = previous.lang;
        document.documentElement.dir = previous.dir;
      });
    },
    [i18n],
  );

  return {
    t,
    currentLanguage,
    currentLanguageLabel: LANGUAGE_LABELS[currentLanguage] ?? 'English',
    isRtl,
    setLanguage,
    languages: LANGUAGE_OPTIONS,
  };
}
